import {HubConnectionBuilder, LogLevel} from "@microsoft/signalr"
import {spinControlPanel, stopReels} from "../slotBoard/spin";
import {freeBetsStore, freeSpinStore, spinStore, store} from "../store";
import {
    demoSpinResponseHandler, freeBetAwardHandler, freeBetCountUpdateHandler, freeBetEndHandler,
    initializeGameHandler,
    spinErrorHandler,
    spinResponseHandler
} from "./responseHandlers";
import {hideReconnectingModal, showReconnectingModal} from "../notifications/reconnectingModal/reconnectingModal";
import {showConnectionLostModal} from "../notifications/connectionLostModal/connectionLostModal";
import {continueAutoSpin} from "../controlPanel/autoSpin";
import {freeBetGame} from "../freeBetGame";
import { getGameOriginDomain } from "../utilites";

const baseUrl =
  `${process.env.REACT_APP_BASE_URL}.${getGameOriginDomain()}` ||
  "https://games-api.fostcraft.com";

class SocketService {
    connection = null;
    reconnectAttempts = 0;
    maxReconnectAttempts = 5;
    reconnectTimeout = 3000;

    constructor() {
        this.initializeConnection()
    }

    isConnectionOpen() {
        return this.connection && this.connection.state === 'Connected';
    }

    initializeConnection() {
        this.connection = new HubConnectionBuilder()
            .withUrl(`${baseUrl}/hubs/game`, {
                accessTokenFactory: () => store.accessToken,
            })
            .configureLogging(LogLevel.Information)
            .build()

        this.connection.onreconnecting(showReconnectingModal);
        this.connection.onreconnected(hideReconnectingModal);
        this.connection.onclose(this.handleConnectionClosed);

        this.connection.on('Spin', spinResponseHandler);
        this.connection.on('SpinError', spinErrorHandler);
        this.connection.on('DemoSpinError', spinErrorHandler);
        this.connection.on('DemoSpin', demoSpinResponseHandler);
        this.connection.on('InitializeGame', initializeGameHandler);
        this.connection.on('FreeBetAward', freeBetAwardHandler);
        this.connection.on('FreeBetCountUpdate', freeBetCountUpdateHandler);
        this.connection.on('FreeBetEnd', freeBetEndHandler);
    }

    startConnection() {
        if (this.isConnectionOpen()) {
            return Promise.resolve();
        }
        return this.connection.start().then(this.initializeGame).catch(this.handleConnectionClosed);
    }

    stopConnection = () => {
        this.connection.stop();
    }

    handleConnectionClosed = (err) => {
        if (store.isSessionExpired) {
            return;
        }
        console.log('Connection closed:', err);

        if (this.reconnectAttempts < this.maxReconnectAttempts) {
            this.reconnectAttempts++;
            console.log('Reconnecting attempt', this.reconnectAttempts);
            showReconnectingModal();

            setTimeout(() => {
                this.connection.start()
                    .then(() => {
                        this.reconnectAttempts = 0;
                        hideReconnectingModal();
                        continueAutoSpin();
                    })
                    .catch(reconnectError => {
                        this.handleConnectionClosed();
                        console.log('Reconnect error:', reconnectError);
                    });
            }, this.reconnectTimeout);
        } else {
            showConnectionLostModal();
            console.log('Max reconnect attempts reached.');
        }
    }

    spin(amount) {
        console.log("spin:" + amount);
        if (!(socketService.connection && socketService.connection.state === 'Connected')) {
            return;
        } else if (store.isDemo()) {
            this.demoSpin(amount);
            return;
        }

        const {gameId, partnerId} = store;
        const freeSpinId = freeSpinStore.freeSpin?.id;
        const isFreeBetSpin = freeBetGame.isFreeBetSpin();
        socketService.connection?.invoke('Spin', {
            gameId, amount, partnerId, freeSpinId,
            freeBetId: isFreeBetSpin ? freeBetsStore.freeBet.id : null,
        });

        if (isFreeBetSpin) {
            freeBetsStore.minusFreeBet();
            freeBetGame.updateFreeSpinLeftText();
        } else if(!freeSpinStore.isBonusGameStarted) {
            spinStore.setUserBalance(spinStore.userBalance - amount);
        }
    }

    demoSpin(amount) {
        const {gameId, partnerId} = store;
        socketService.connection?.invoke('DemoSpin', {
            gameId,
            amount,
            partnerId,
            isBonus: freeSpinStore.isBonusGameStarted
        });
        !freeSpinStore.isBonusGameStarted && spinStore.setUserBalance(spinStore.userBalance - amount);
    }

    initializeGame() {
        const {gameId, partnerId} = store;
        socketService.connection?.invoke('InitializeGame', {gameId, partnerId, isDemoMode: store.isDemo()});
    }

}

export const socketService = new SocketService()
